import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
} from '@shigo-ui/dashboard-ui';
import { Button, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useDealMutations, useDealsQuery, useDealStatusCountsQuery } from '../../hooks';
import {
  dealFormValueToPayload,
  dealPageSizes,
  dealStatusLabel,
  dealToFormValue,
  DealSortDirection,
  DealStatus,
  isDealSortProperty,
  type Deal,
} from '../../model';
import { useDealsRouteFilters } from '../../useRouteFilters';
import { createDealsTableColumns } from './columns';

export function DealsTable() {
  const { can } = useShigoPermissions();
  const routeFilters = useDealsRouteFilters();
  const { filter } = routeFilters;
  const query = useDealsQuery(filter);
  const counts = useDealStatusCountsQuery(filter.searchTerm);
  const mutations = useDealMutations();
  const search = useDebouncedValue(
    filter.searchTerm,
    (value) => {
      routeFilters.setSearch(value);
    },
    650,
  );
  const [pendingIds, setPendingIds] = React.useState<Set<string>>(() => new Set());
  const canCreate = can(ShigoPermission.DemandDealsCreate);
  const canEdit = can(ShigoPermission.DemandDealsEdit);
  const canArchive = can(ShigoPermission.DemandDealsArchive);
  const columns = createDealsTableColumns({
    canEdit,
    canArchive,
    pendingIds,
    onStatusChange: (deal) => void changeStatus(deal),
  });

  async function changeStatus(deal: Deal) {
    setPendingIds((current) => new Set(current).add(deal.id));
    try {
      const value = dealToFormValue(deal);
      value.status = deal.status === DealStatus.Archived ? DealStatus.Active : DealStatus.Archived;
      await mutations.update.mutateAsync({
        id: deal.id,
        payload: dealFormValueToPayload(value, deal),
      });
      toast.success(value.status === DealStatus.Archived ? 'Deal archived.' : 'Deal restored.');
    } catch (error) {
      showRequestError(error, 'Deal status could not be changed.');
    } finally {
      setPendingIds((current) => {
        const next = new Set(current);
        next.delete(deal.id);
        return next;
      });
    }
  }

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title="Deals"
        tableLabel="Deals"
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(deal) => deal.id}
        loading={query.isLoading}
        loadingMessage="Loading deals"
        errorMessage={query.isError ? 'Deals could not be loaded.' : undefined}
        emptyMessage="No deals found."
        minWidthClassName="min-w-[1260px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label="Search deals"
              placeholder="Search deals"
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DashboardTableMultiSelectFilter
              ariaLabel="Filter deals by status"
              label={
                filter.statuses.length
                  ? filter.statuses.map(dealStatusLabel).join(', ')
                  : 'All statuses'
              }
              value={filter.statuses}
              options={Object.values(DealStatus).map((status) => ({
                value: status,
                label: dealStatusLabel(status),
                count: counts.data?.[status] ?? 0,
              }))}
              onChange={(statuses) => {
                routeFilters.setStatuses(statuses);
              }}
            />
            {canCreate ? (
              <Button asChild>
                <Link to="/demand/deals/create">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  New deal
                </Link>
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: dealPageSizes,
          disabled: query.isFetching,
          onChange: (value) => {
            routeFilters.setPagination(value.page, value.pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === DealSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (value) => {
            if (!value) {
              routeFilters.setSort(null, filter.sortDirection);
              return;
            }
            if (!isDealSortProperty(value.key)) return;
            routeFilters.setSort(
              value.key,
              value.direction === DashboardDataTableSortDirection.Asc
                ? DealSortDirection.Asc
                : DealSortDirection.Desc,
            );
          },
        }}
      />
    </main>
  );
}
